import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import appI18n from "@/i18n";
import Sidebar from "@/layout/Sidebar";
import TopNavbar from "@/layout/TopNavbar";
import ScoreCard from "@/charts/ScoreCard";
import DonutChart from "@/charts/DonutChart";
import TwoColumnTable from "@/charts/TwoColumnTable";
import FourColumnTable from "@/charts/FourColumnTable";
import TopCitationCompetitorsHeader from "@/components/dashboard/TopCitationCompetitorsHeader";
import ToggleInput from "@/components/ui/ToggleInput";
import NColumnTable from "@/charts/NColumnTable";
import TrackingChart from "@/charts/TrackingChart";
import { useNavigate, useLocation } from "react-router-dom";
import usePostQuery from "@/hooks/usePostQuery";
import apiClient from "@/services/apiClient";
import useLlmPlatformFilter from "@/hooks/useLlmPlatformFilter";
import { getLocalStorageWithExpiry, showSuccess, showApiError, clearAuthStorage } from "@/utils";
import { CLIENT_ID_KEY } from "@/const";
import { Copy, SquareArrowOutUpRight } from "lucide-react";
import CustomDropdown from "@/components/ui/CustomDropdown";
import SectionHeading from "@/components/ui/SectionHeading";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import "@/pages/page.css";
import { BACKEND_URL } from "@/config";

const { RangePicker } = DatePicker;


function GEODashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const client_id = getLocalStorageWithExpiry(CLIENT_ID_KEY);
  const [clientDetails, setClientDetails] = useState(null);
  const [clientDetailsError, setClientDetailsError] = useState(null);
  const [isClientDetailsLoading, setIsClientDetailsLoading] = useState(
    Boolean(client_id),
  );
  const [ownBrandNameVariations, setOwnBrandNameVariations] = useState([]);
  const TrackingWeekMinCount = 3; // Minimum number of weeks with data to show tracking chart
  const TrackingChartCheckpoints = 7; // Number of checkpoints to show in tracking chart
  useEffect(() => {
    if (!client_id) {
      setClientDetails(null);
      setClientDetailsError(null);
      setIsClientDetailsLoading(false);
      return;
    }

    const fetchClientDetails = async () => {
      setIsClientDetailsLoading(true);
      setClientDetailsError(null);
      try {
        const response = await apiClient.get(
          `${BACKEND_URL}/api/v1/client/${client_id}`,
        );

        setClientDetails(response.data);
        setClientDetailsError(null);
      } catch (error) {
        console.error("Error fetching client details:", error);
        showApiError(
          appI18n.t(
            "dashboard.clientDetailsUnavailable",
            "Client details could not be loaded. Some brand-specific widgets may be unavailable until this is resolved.",
          ),
        );
        setClientDetails(null);
        setClientDetailsError(error);
      } finally {
        setIsClientDetailsLoading(false);
      }
    };

    fetchClientDetails();
  }, [client_id]);

  useEffect(() => {
    if (!client_id) return;

    const fetchBrandNameVariations = async () => {
      try {
        const response = await apiClient.get(
          `${BACKEND_URL}/api/v1/brand/name-variations/${client_id}`,
        );

        setOwnBrandNameVariations(response.data?.brand_name_variations || []);
      } catch (error) {
        console.error("Failed to fetch brand name variations", error);
        setOwnBrandNameVariations([]);
      }
    };

    fetchBrandNameVariations();
  }, [client_id]);

  const brandName = clientDetails?.data?.own_brand || null;
  const [checkPoint, setCheckPoint] = useState(7); // days - Weekly
  const [isCustomDateRangeMode, setIsCustomDateRangeMode] = useState(false);
  const [customDateRange, setCustomDateRange] = useState(null); // [dayjs, dayjs] | null
  const [isDateRangePickerOpen, setIsDateRangePickerOpen] = useState(false);
  const [activeCustomDateRangeInput, setActiveCustomDateRangeInput] =
    useState(null);
  // /api/v2/geo-dashboard endpoints take an explicit start_date/end_date range
  // instead of a checkpoint day count. The backend rejects end_date === today
  // when today is Sunday (UTC), so fall back to yesterday in that case.
  const getUtcEndDate = () => {
    const now = new Date();
    const end = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
    );
    if (now.getUTCDay() === 0) {
      end.setUTCDate(end.getUTCDate() - 1);
    }
    return end;
  };

  // Reinterpret a UTC-anchored calendar day (e.g. endDate above) as a local
  // midnight Date carrying the same Y/M/D, so dayjs 'day' comparisons against
  // picker values (which are calendar days in the viewer's local timezone)
  // aren't thrown off by the viewer's UTC offset.
  const toLocalCalendarDate = (utcDate) =>
    new Date(
      utcDate.getUTCFullYear(),
      utcDate.getUTCMonth(),
      utcDate.getUTCDate(),
    );

  // The backend treats the current period as an inclusive day range whose
  // end_utc is midnight(end) + 24h (an exclusive upper bound), so a window of N
  // days requires start = end - (N - 1) days for the resulting delta to equal N
  // (and stay divisible by 7, per validate_date_range).
  const endDate = getUtcEndDate();
  const presetStartDate = new Date(endDate);
  presetStartDate.setUTCDate(presetStartDate.getUTCDate() - (checkPoint - 1));

  const hasCustomRange =
    isCustomDateRangeMode && customDateRange?.[0] && customDateRange?.[1];
  const effectiveStartDate = hasCustomRange
    ? new Date(
        Date.UTC(
          customDateRange[0].year(),
          customDateRange[0].month(),
          customDateRange[0].date(),
        ),
      )
    : presetStartDate;
  const effectiveEndDate = hasCustomRange
    ? new Date(
        Date.UTC(
          customDateRange[1].year(),
          customDateRange[1].month(),
          customDateRange[1].date(),
        ),
      )
    : endDate;

  // /api/v2 compares a current period against a comparison period of the same
  // length made up of the N calendar days immediately before it (so "7 days"
  // compares against the prior 7, "14" against the prior 14, ...). The
  // comparison period ends the day before the current period starts, which
  // keeps the two ranges from overlapping and keeps its span divisible by 7.
  const buildV2Periods = (startDate, rangeEndDate) => {
    const spanDays = Math.round((rangeEndDate - startDate) / 86400000) + 1;
    const comparisonEndDate = new Date(startDate);
    comparisonEndDate.setUTCDate(comparisonEndDate.getUTCDate() - 1);
    const comparisonStartDate = new Date(comparisonEndDate);
    comparisonStartDate.setUTCDate(
      comparisonStartDate.getUTCDate() - (spanDays - 1),
    );
    return {
      current_period_start_date: startDate.toISOString(),
      current_period_end_date: rangeEndDate.toISOString(),
      comparison_period_start_date: comparisonStartDate.toISOString(),
      comparison_period_end_date: comparisonEndDate.toISOString(),
    };
  };

  const v2Periods = buildV2Periods(effectiveStartDate, effectiveEndDate);

  // The max selectable date in the picker mirrors endDate's Sunday exception:
  // "today" is only ever off-limits when it's a Sunday.
  const maxAllowedPickerDate = dayjs(toLocalCalendarDate(endDate));

  // First click picks the start date freely (up to the max allowed date).
  // Once a start date is set (info.from), only end dates whose inclusive
  // span from start is a multiple of 7 days remain selectable — matching the
  // backend's validate_date_range divisible-by-7 requirement.
  const isDateRangeDateDisabled = (current, info) => {
    if (!current) return false;
    if (current.isAfter(maxAllowedPickerDate, "day")) return true;

    const from = info?.from;
    if (from) {
      const diffDays = current.startOf("day").diff(from.startOf("day"), "day");
      if (diffDays < 0) return true;
      return diffDays % 7 !== 6;
    }

    if (activeCustomDateRangeInput === "start" && customDateRange?.[1]) {
      return current.isAfter(customDateRange[1], "day");
    }
    if (activeCustomDateRangeInput === "end" && customDateRange?.[0]) {
      return current.isBefore(customDateRange[0], "day");
    }
    return false;
  };

  // Trend charts render a fixed 7-week window, independent of the checkPoint
  // filter and any custom date range (the backend clamps this to [6, 10] weeks
  // regardless). Both boundaries stay anchored to endDate so the window is
  // always the most recent 7 weeks with a start that precedes its end.
  const trendStartDate = new Date(endDate);
  trendStartDate.setUTCDate(
    trendStartDate.getUTCDate() - (TrackingChartCheckpoints * 7 - 1),
  );
  const v2TrendPeriods = buildV2Periods(trendStartDate, endDate);

  // Search engine filter, limited to the client's pricing tier
  const {
    llmPlatforms: LLM_Platforms,
    setLlmPlatforms,
    engineOptions,
    isPending: isLlmProvidersPending,
  } = useLlmPlatformFilter(client_id);
  const [isPermanentlyExpanded, setIsPermanentlyExpanded] = useState(true);
  const [isOnlyBusinessCompetitor, setIsOnlyBusinessCompetitor] =
    useState(false);
  const [isOwnDomainOnly, setIsOwnDomainOnly] = useState(false);
  const [brandFilter, setBrandFilter] = useState("all"); // "all" | "branded" | "unbranded"
  const [funnelStageFilter, setFunnelStageFilter] = useState("all"); // "all" | "top" | "middle" | "bottom"

  const isBrandedParam =
    brandFilter === "branded"
      ? true
      : brandFilter === "unbranded"
        ? false
        : undefined;

  const funnelStageParam =
    funnelStageFilter === "all" ? undefined : funnelStageFilter;

  const [isDownloadingXlsx, setIsDownloadingXlsx] = useState(false);

  const handleDownloadXLSX = async () => {
    // The v1 report endpoint only accepts a checkpoint day count, so it can't
    // represent a custom date range. Guard here in addition to disabling the
    // button so the report can never silently cover a different period than the
    // dashboard.
    if (isCustomDateRangeMode) {
      showApiError(
        "Report download isn't available for custom date ranges yet. Switch to a weekly checkpoint to download a report.",
      );
      return;
    }
    setIsDownloadingXlsx(true);
    try {
      const payload = {
        checkpoint: checkPoint,
        llm_providers: LLM_Platforms, // live state
        is_branded: isBrandedParam ?? null,
        funnel_stage: funnelStageParam ?? null,
        kpis: [
          "total_citations",
          "share_of_citation",
          "brand_citations",
          "top_cited_pages",
          "top_topics",
          "total_mentions",
          "share_of_mention",
        ],
      };

      const response = await apiClient.post(
        `${BACKEND_URL}/api/v1/reports/download`,
        payload,
        { responseType: "blob" }
      );

      const blob = response.data;
      const disposition = response.headers
        ? typeof response.headers.get === "function"
          ? response.headers.get("content-disposition")
          : response.headers["content-disposition"]
        : null;

      let filename = `GEO_Performance_Report_${new Date()
        .toISOString()
        .slice(0, 10)
        .replace(/-/g, "")}.xlsx`;
      const match =
        disposition &&
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
      if (match && match[1]) filename = match[1].replace(/['"]/g, "");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      showSuccess(
        t("dashboard.reportDownloadedSuccess", "Report downloaded successfully."),
      );
    } catch (error) {
      if (error.response?.status === 401) {
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return;
      }
      console.error("Report download error:", error);
      showApiError(
        error.response?.data?.detail ||
          error.message ||
          t(
            "dashboard.reportDownloadError",
            "An error occurred while generating your report.",
          ),
      );
    } finally {
      setIsDownloadingXlsx(false);
    }
  };
  const handleCopyUrl = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      showSuccess(t("common.copied", "URL copied to clipboard!"));
    } catch (err) {
      console.error("Failed to copy URL:", err);
      showApiError(t("common.copyFailed", "Failed to copy URL."));
    }
  };

  // Fetch Data
  const skipApi = !client_id || isClientDetailsLoading || isLlmProvidersPending;

  const [citationOverview, citationOverviewIsLoaded, citationOverviewError] =
    usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/citation-overview`, {
      ...v2Periods,
      llm_providers: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    }, { skip: skipApi });

  // Mention Overview
  const [mentionOverview, mentionOverviewIsLoaded, mentionOverviewError] =
    usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/mention-overview`, {
      ...v2Periods,
      llm_providers: LLM_Platforms,
      brand_name: brandName,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    }, { skip: skipApi });

  const [CitationData, citationDataIsLoaded, citationDataError] = usePostQuery(
    `${BACKEND_URL}/api/v2/geo-dashboard/citation`,
    {
      ...v2Periods,
      llm_providers: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    },
    { skip: skipApi },
  );

  // Filter out for only business competitors
  const filteredCitationData = CitationData.filter(
    (item) =>
      (isOnlyBusinessCompetitor && item.is_business_competitor) ||
      (isOnlyBusinessCompetitor && item.is_own_domain) ||
      !isOnlyBusinessCompetitor,
  );

  // Create annotated citation data with formatted values
  const annotatedCitationData = filteredCitationData.map((item) => ({
    // brand: `<b>${item.brand_name}</b><br><small>${item.brand_url}</small>`,
    // Mahdee Note: Currently hhiding the brand_url untill hallucination is fixed
    brand: `${item.brand_name}`,
    citation_count: item.current_period_citation_count,
    share_of_citation: `${item.current_period_share_of_citation}%`,
    citation_change:
      item.citation_count_change >= 0
        ? `<span class="text-success"><i class="bi bi-arrow-up-right"></i> ${item.citation_count_change}%</span>`
        : `<span class="text-danger"><i class="bi bi-arrow-down-right"></i> ${Math.abs(
            item.citation_count_change,
          )}%</span>`,
  }));

  // Top Performing URLs
  const [TopPerformingURLs, TopPerformingURLsIsLoaded, TopPerformingURLsError] =
    usePostQuery(
      `${BACKEND_URL}/api/v2/geo-dashboard/citation/top-cited-pages`,
      {
        ...v2Periods,
        llm_providers: LLM_Platforms,
        brand_name: brandName,
        ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
        ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
      },
      { skip: skipApi },
    );
  const filteredTopPerformingURLs = TopPerformingURLs.filter(
    (item) => (isOwnDomainOnly && item.is_own_domain) || !isOwnDomainOnly,
  );

  // annonate citation_count_change
  const annotatedTopPerformingURLs = filteredTopPerformingURLs.map((item) => ({
    url: item.url.replace(/^(https?:\/\/)/, ""),
    link: (
      <div className="d-flex justify-content-end align-items-center gap-1 show-on-row-hover flex-shrink-0">
        <button
          type="button"
          className="btn p-0 border-0 bg-transparent d-flex align-items-center"
          onClick={() => handleCopyUrl(item.url)}
          aria-label="Copy URL"
          title="Copy URL"
        >
          <Copy size={16} style={{ color: "var(--action-primary)" }} />
        </button>
        <a
          href={item.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none d-flex align-items-center"
          aria-label="Open URL in new tab"
          title="Open in new tab"
        >
          <SquareArrowOutUpRight
            size={18}
            style={{ color: "var(--action-primary)" }}
          />
        </a>
      </div>
    ),
    citation_count: item.current_period_citation_count,
    is_own_domain: item.is_own_domain,
    citation_count_change:
      item.citation_count_change >= 0
        ? `<span class="text-success"><i class="bi bi-arrow-up-right"></i> ${item.citation_count_change}%</span>`
        : `<span class="text-danger"><i class="bi bi-arrow-down-right"></i> ${Math.abs(
            item.citation_count_change,
          )}%</span>`,
  }));

  // Top Topic For Citations
  const [
    TopTopicForCitations,
    TopTopicForCitationsIsLoaded,
    TopTopicForCitationsError,
  ] = usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/citation/top-topics`, {
    ...v2Periods,
    llm_providers: LLM_Platforms,
    brand_name: brandName,
    ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
    ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
  }, { skip: skipApi });

  // Mention Data
  const [MentionData, mentionDataIsLoaded, mentionDataError] = usePostQuery(
    `${BACKEND_URL}/api/v2/geo-dashboard/mention`,
    {
      ...v2Periods,
      llm_providers: LLM_Platforms,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    },
    { skip: skipApi },
  );

  // Filter out for only business competitors
  const filteredMentionData = MentionData.filter(
    (item) =>
      (isOnlyBusinessCompetitor && item.is_business_competitor) ||
      (isOnlyBusinessCompetitor && item.is_own_brand) ||
      !isOnlyBusinessCompetitor,
  );

  // anonate mention data
  const annotatedMentionData = filteredMentionData.map((item) => ({
    // brand: `<b>${item.brand_name}</b><br><small>${item.brand_url}</small>`,
    // temporarily hiding brand_url until hallucination is fixed
    brand: `${item.brand_name}`,
    mention_count: item.current_period_mention_count,
    share_of_mention: `${item.current_period_share_of_mention}%`,
    mention_change:
      item.mention_count_change >= 0
        ? `<span class="text-success"><i class="bi bi-arrow-up-right"></i> ${item.mention_count_change}%</span>`
        : `<span class="text-danger"><i class="bi bi-arrow-down-right"></i> ${Math.abs(
            item.mention_count_change,
          )}%</span>`,
  }));

  // My Top Mentions
  const [myTopMentions, myTopMentionsIsLoaded, myTopMentionsError] =
    usePostQuery(
      `${BACKEND_URL}/api/v2/geo-dashboard/mention/my-top-mentions`,
      {
        ...v2Periods,
        llm_providers: LLM_Platforms,
        brand_name: brandName,
        ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
        ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
      },
      { skip: skipApi },
    );

  const enhancedMyTopMentions =
    myTopMentions?.map((mention) => {
      if (!ownBrandNameVariations || ownBrandNameVariations.length === 0) {
        return mention;
      }

      // Create a regex pattern that matches any of the brand variations
      const escapedVariations = ownBrandNameVariations
        .sort((a, b) => b.length - a.length) // Sort by length, longest first
        .map(
          (variant) =>
              variant
                .replace(/[.*+?^${}()|[\]\\]/g, "\\$&") // Escape special regex characters
                .replace(/\s+/g, "\\s*") // Allow flexible whitespace matching
                .replace(/\\\./g, "\\.?"), // Make periods optional
        );

      const pattern = `\\b(${escapedVariations.join("|")})\\b`;

      // Highlight all brand name variations in the mention_text
      const highlightedMentionText = mention.mention_text.replace(
        new RegExp(pattern, "gi"),
        (match) => `<strong>${match}</strong>`,
      );

      return {
        ...mention,
        mention_text: highlightedMentionText,
      };
    }) || [];

  // Top Topic For Mentions
  const [
    TopTopicForMentions,
    TopTopicForMentionsIsLoaded,
    TopTopicForMentionsError,
  ] = usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/mention/top-topics`, {
    ...v2Periods,
    llm_providers: LLM_Platforms,
    brand_name: brandName,
    ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
    ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
  }, { skip: skipApi });

  const toggleSidebar = () => {
    setIsPermanentlyExpanded(!isPermanentlyExpanded);
  };

  // Citation trend data
  const [citationTrendData, citationTrendDataIsLoaded, citationTrendDataError] =
    usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/citation-trend`, {
      ...v2TrendPeriods,
      llm_providers: LLM_Platforms,
      brand_name: brandName,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    }, { skip: skipApi });

  // Mention trend data
  const [mentionTrendData, mentionTrendDataIsLoaded, mentionTrendDataError] =
    usePostQuery(`${BACKEND_URL}/api/v2/geo-dashboard/mention-trend`, {
      ...v2TrendPeriods,
      llm_providers: LLM_Platforms,
      brand_name: brandName,
      ...(isBrandedParam !== undefined && { is_branded: isBrandedParam }),
      ...(funnelStageParam !== undefined && { funnel_stage: funnelStageParam }),
    }, { skip: skipApi });

  // Extract variables AFTER data loads (these will re-compute when data changes)
  const citationTrackingResult = citationTrendData?.tracking_result || [];
  const citationColumnNames = citationTrendData?.column_names || [];
  const citationDateColumns = citationColumnNames.filter(
    (col) => col !== "brand_name",
  );
  const citationColumnTitles = citationColumnNames.map((col, idx) =>
    idx === 0 ? "Brand" : col,
  );

  const mentionTrackingResult = mentionTrendData?.tracking_result || [];
  const mentionColumnNames = mentionTrendData?.column_names || [];
  const mentionDateColumns = mentionColumnNames.filter(
    (col) => col !== "brand_name",
  );
  const mentionColumnTitles = mentionColumnNames.map((col, idx) =>
    idx === 0 ? "Brand" : col,
  );

  // Calculate valid week counts
  const citation_valid_week_count = citationDateColumns.filter((dateCol) => {
    // Check if this week has at least one non-dash value across all brands
    return citationTrackingResult.some((row) => {
      const value = row[dateCol];
      return (
        value !== "-" && value !== null && value !== undefined && value !== ""
      );
    });
  }).length;

  const mention_valid_week_count = mentionDateColumns.filter((dateCol) => {
    // Check if this week has at least one non-dash value across all brands
    return mentionTrackingResult.some((row) => {
      const value = row[dateCol];
      return (
        value !== "-" && value !== null && value !== undefined && value !== ""
      );
    });
  }).length;

  // Filter handlers
  const handleDateRangeChange = (value) => {
    if (value === "Custom date range") {
      setIsCustomDateRangeMode(true);
      setIsDateRangePickerOpen(true);
      return;
    }

    setIsCustomDateRangeMode(false);
    setCustomDateRange(null);
    switch (value) {
      case "Weekly checkpoints":
        setCheckPoint(7);
        break;
      case "4-weekly checkpoints":
        setCheckPoint(28);
        break;
      case "12-weekly checkpoints":
        setCheckPoint(84);
        break;
      default:
        setCheckPoint(7);
    }
  };

  const dropdownValue = isCustomDateRangeMode
    ? "Custom date range"
    : checkPoint === 7
      ? "Weekly checkpoints"
      : checkPoint === 28
        ? "4-weekly checkpoints"
        : "12-weekly checkpoints";

  const dateRangeOptions = [
    { value: "Weekly checkpoints", label: t("dashboard.last7Days", "Last 7 days") },
    { value: "4-weekly checkpoints", label: t("dashboard.last28Days", "Last 28 days") },
    { value: "12-weekly checkpoints", label: t("dashboard.last12Weeks", "Last 12 weeks") },
    { value: "Custom date range", label: t("dashboard.customRange", "Custom range") },
  ];

  const engineAllOption = {
    value: "all",
    label: t("dashboard.allSearchEngines", "All search engines"),
  };

  const queryOptions = [
    { value: "all", label: t("dashboard.allQueries", "All queries") },
    { value: "branded", label: t("dashboard.brandedOnly", "Branded only") },
    { value: "unbranded", label: t("dashboard.unbrandedOnly", "Unbranded only") },
  ];

  const funnelStageOptions = [
    { value: "all", label: t("dashboard.allFunnelStages", "All funnel stages") },
    { value: "top", label: t("tracking.topFunnel", "Top") },
    { value: "middle", label: t("tracking.middleFunnel", "Middle") },
    { value: "bottom", label: t("tracking.bottomFunnel", "Bottom") },
  ];

  return (
    <div className="app-container">
      <Sidebar
        currentPage={"geo-dashboard"}
        isPermanentlyExpanded={isPermanentlyExpanded}
      />
      <main className="content dashboard-content bg-page">
        <div className="container-fluid p-4">
          <TopNavbar
            isPermanentlyExpanded={isPermanentlyExpanded}
            toggleSidebar={toggleSidebar}
          />

          {/* Heading  */}
          <div className="d-flex justify-content-between align-items-start mb-3 flex-wrap gap-2">
            <div>
              <h3 className="h4 fw-bold text-dark mb-0">
                {t("dashboard.title", "GEO Dashboard")}
              </h3>
              <p className="mt-1 fs-6 text-secondary mb-0">
                {t(
                  "dashboard.subtitle",
                  "Track performance across GenAI search engines and optimize your content strategy",
                )}
              </p>
            </div>
            <button
              data-tour="geo-dashboard.download"
              onClick={handleDownloadXLSX}
              disabled={
                isDownloadingXlsx ||
                isCustomDateRangeMode ||
                isLlmProvidersPending
              }
              className="btn-download-report"
              title={
                isCustomDateRangeMode
                  ? "Report download isn't available for custom date ranges yet. Switch to a weekly checkpoint to download a report."
                  : undefined
              }
            >
              {isDownloadingXlsx ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("common.generatingReport", "Generating Report...")}
                </>
              ) : (
                <>
                  <i className="bi bi-download"></i>
                  {t("common.downloadReport", "Download Report")}
                </>
              )}
            </button>
          </div>

          {/* Filter Area */}
          <div className="geo-filter-panel mb-4" data-tour="geo-dashboard.filters">
            <div className="geo-filter-header">
              <div className="geo-filter-heading">
                <span className="geo-filter-icon" aria-hidden="true">
                  <i className="bi bi-sliders2"></i>
                </span>
                <div>
                  <h2>{t("dashboard.filterTitle", "Filter dashboard")}</h2>
                  <p>{t("dashboard.filterHint", "Set the reporting window and refine the results.")}</p>
                </div>
              </div>
            </div>

            <div className="geo-filter-grid">
              <div className="geo-filter-field">
                <label>{t("dashboard.dateRange", "Date range")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  options={dateRangeOptions}
                  value={dropdownValue}
                  onChange={handleDateRangeChange}
                />
              </div>

              {isCustomDateRangeMode && (
                <div className="geo-filter-field">
                  <label>{t("dashboard.customDateRange", "Custom date range")}</label>
                  <RangePicker
                    open={isDateRangePickerOpen}
                    onOpenChange={(open) => {
                      setIsDateRangePickerOpen(open);
                      if (!open) setActiveCustomDateRangeInput(null);
                    }}
                    value={customDateRange}
                    onFocus={(_, info) => setActiveCustomDateRangeInput(info?.range ?? null)}
                    onCalendarChange={(_, __, info) => setActiveCustomDateRangeInput(info?.range ?? null)}
                    onChange={(dates) => {
                      setCustomDateRange(dates);
                      if (dates?.[0] && dates?.[1]) {
                        setActiveCustomDateRangeInput(null);
                        setIsDateRangePickerOpen(false);
                      }
                    }}
                    disabledDate={isDateRangeDateDisabled}
                    allowClear={false}
                    format="YYYY-MM-DD"
                    separator={
                      <i className="bi bi-arrow-right geo-range-arrow" aria-hidden="true"></i>
                    }
                  />
                </div>
              )}

              <div className="geo-filter-field">
                <label>{t("dashboard.searchEngine", "Search engine")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  multiple
                  allOption={engineAllOption}
                  placeholder={t("dashboard.selectSearchEngines", "Select search engines")}
                  selectAllLabel={t("common.selectAll", "Select all")}
                  deselectAllLabel={t("common.deselectAll", "Deselect all")}
                  doneLabel={t("common.done", "Done")}
                  options={engineOptions}
                  value={LLM_Platforms}
                  onChange={setLlmPlatforms}
                />
              </div>

              <div className="geo-filter-field">
                <label>{t("dashboard.queryType", "Query type")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  options={queryOptions}
                  value={brandFilter}
                  onChange={setBrandFilter}
                />
              </div>

              <div className="geo-filter-field">
                <label>{t("dashboard.funnelStage", "Funnel stage")}</label>
                <CustomDropdown
                  className="geo-filter-dropdown"
                  options={funnelStageOptions}
                  value={funnelStageFilter}
                  onChange={setFunnelStageFilter}
                />
              </div>
            </div>
          </div>

          {clientDetailsError && (
            <div className="alert alert-warning" role="alert">
              {t(
                "dashboard.clientDetailsUnavailable",
                "Client details could not be loaded. Some brand-specific widgets may be unavailable until this is resolved.",
              )}
            </div>
          )}

          {/* Scorecards */}
          <div className="row" data-tour="geo-dashboard.kpis">
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t("dashboard.totalCitations", "Total Citations")}
                value={
                  citationOverview &&
                  citationOverview.current_period_total_citations
                }
                icon="bi bi-link-45deg"
                change={citationOverview && citationOverview.citation_change}
                isLoaded={citationOverviewIsLoaded}
                error={citationOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t(
                  "dashboard.brandCitationShare",
                  "Brand Citation Share",
                )}
                value={
                  (citationOverview &&
                    citationOverview.current_period_share_of_citation) + "%"
                }
                change={
                  citationOverview && citationOverview.share_of_citation_change
                }
                icon="bi bi-clock-history"
                isLoaded={citationOverviewIsLoaded}
                error={citationOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t(
                  "dashboard.totalBrandMentions",
                  "Total Brand Mentions",
                )}
                value={
                  mentionOverview &&
                  mentionOverview.current_period_total_mentions
                }
                icon="bi bi-globe2"
                change={mentionOverview && mentionOverview.mention_change}
                isLoaded={mentionOverviewIsLoaded}
                error={mentionOverviewError}
              />
            </div>
            <div className="col-lg-3 col-md-6 mb-4">
              <ScoreCard
                title={t(
                  "dashboard.brandMentionShare",
                  "Brand Mention Share",
                )}
                value={
                  (mentionOverview &&
                    mentionOverview.current_period_share_of_mention) + "%"
                }
                icon="bi bi-people"
                change={
                  mentionOverview && mentionOverview.share_of_mention_change
                }
                isLoaded={mentionOverviewIsLoaded}
                error={mentionOverviewError}
              />
            </div>
          </div>
          <br />

          {/* Citation insights */}
          <div
            className="d-flex justify-content-between align-items-center"
            data-tour="geo-dashboard.citation-insights"
          >
            <SectionHeading>
              {t("dashboard.citationInsights", "Citation Insights")}
            </SectionHeading>
            <ToggleInput
              label={t(
                "dashboard.onlyBusinessCompetitors",
                "Business Competitors Only",
              )}
              checked={isOnlyBusinessCompetitor}
              onChange={(e) => setIsOnlyBusinessCompetitor(e.target.checked)}
            />
          </div>
          <br />

          <div className="row mb-4">
            <div className="col-lg-6 d-flex" data-tour="geo-dashboard.citation-distribution">
              <DonutChart
                title={t(
                  "dashboard.citationDistributionByDomain",
                  "Citation Distribution by Domain",
                )}
                data={filteredCitationData || []}
                keyColumn="brand_name"
                valueColumn="current_period_citation_count"
                pinColumn={"is_own_domain"}
                isLoaded={citationDataIsLoaded}
                error={citationDataError}
                height="100%"
              />
            </div>

            <div className="col-lg-6" data-tour="geo-dashboard.citation-domains">
              <FourColumnTable
                data={annotatedCitationData || []}
                title={t(
                  "dashboard.topCitationCompetitors",
                  "Top Citation Competitor Domains",
                )}
                column1Key={"brand"}
                column1Title={t("dashboard.brandName", "Brand name")}
                column2Key={"citation_count"}
                column2Title={t("dashboard.citations", "Citations")}
                column3Key={"share_of_citation"}
                column3Title={t("dashboard.share", "Share")}
                column4Key={"citation_change"}
                column4Title={t("dashboard.change", "Change")}
                isLoaded={citationDataIsLoaded}
                error={citationDataError}
                columnRatio="25-25-25-25"
                columnAlignment="l-r-r-r"
                // minCellHeight="4rem" // 4rem is needed when we have both brand_name and domain URL
                itemsPerPage={8}
              />
            </div>
          </div>
          <br />

          <div className="row">
            {/* Left Side */}
            <div className="col-lg-6 mb-4" data-tour="geo-dashboard.top-urls">
              <FourColumnTable
                data={annotatedTopPerformingURLs || []}
                title={
                  <TopCitationCompetitorsHeader
                    isOwnDomainOnly={isOwnDomainOnly}
                    setIsOwnDomainOnly={setIsOwnDomainOnly}
                  />
                }
                exportTitle={t(
                  "dashboard.topCitationCompetitors",
                  "Top Citation Competitors",
                )}
                column1Key="url"
                column2Key="link"
                column3Key="citation_count"
                column4Key="citation_count_change"
                column1Title={t("common.url", "URL")}
                column3Title={t("dashboard.citations", "Citations")}
                column4Title={t("dashboard.change", "Change")}
                columnRatio="68-7-10-15"
                columnAlignment="l-r-r-r"
                isLoaded={TopPerformingURLsIsLoaded}
                error={TopPerformingURLsError}
                minCellHeight="3rem"
              />
            </div>
            {/* Right Side */}
            <div className="col-lg-6 mb-4" data-tour="geo-dashboard.citation-topics">
              <FourColumnTable
                data={TopTopicForCitations || []}
                title={t(
                  "dashboard.topTopicsForCitations",
                  "Top Topics for Citations",
                )}
                column1Key="topic"
                column1Title={t("dashboard.topic", "Topic")}
                column2Key="total_queries"
                column2Title={t("dashboard.prompts", "Prompts")}
                column3Key="current_period_total_citations"
                column3Title={t("dashboard.citations", "Citations")}
                column4Key="current_period_ratio"
                column4Title={t("dashboard.ratio", "Ratio")}
                isLoaded={TopTopicForCitationsIsLoaded}
                error={TopTopicForCitationsError}
                columnRatio="55-15-15-15"
                columnAlignment="l-r-r-r"
                minCellHeight="3rem"
              />
            </div>
          </div>

          <br />

          {/* Citation Trend Section */}
          {citation_valid_week_count > TrackingWeekMinCount && (
            <>
              <div className="row" data-tour="geo-dashboard.trends">
                <div className="col-lg-6 mb-4">
                  <NColumnTable
                    data={citationTrackingResult}
                    n={8}
                    columnKeys={citationColumnNames}
                    columnTitles={citationColumnTitles}
                    columnRatio="30-10-10-10-10-10-10-10"
                    columnAlignment="l-r-r-r-r-r-r-r"
                    title={t("dashboard.citationTrend", "Citation Trend")}
                    isLoaded={citationTrendDataIsLoaded}
                    error={citationTrendDataError}
                    boxHeight="350px"
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <TrackingChart
                    title={t(
                      "dashboard.citationTrendOverTime",
                      "Citation Trend Over Time",
                    )}
                    data={citationTrackingResult}
                    queryColumn="brand_name"
                    dateColumns={citationDateColumns}
                    isLoaded={citationTrendDataIsLoaded}
                    error={citationTrendDataError}
                    height={350}
                  />
                </div>
              </div>
              <br />
            </>
          )}

          {/* Mention Insights */}
          <SectionHeading>
            {t("dashboard.mentionInsights", "Mention Insights")}
          </SectionHeading>
          <div className="row">
            <div className="col-lg-6 mb-4"></div>
          </div>

          <div className="row" data-tour="geo-dashboard.mention-insights">
            <div className="col-xl-6 mb-4">
              <DonutChart
                title={t(
                  "dashboard.brandMentionsDistribution",
                  "Brand Mentions Distribution",
                )}
                data={filteredMentionData || []}
                keyColumn="brand_name"
                pinColumn={"is_own_brand"}
                valueColumn="current_period_mention_count"
                isLoaded={mentionDataIsLoaded}
                error={mentionDataError}
                height={392}
              />
            </div>

            <div className="col-xl-6 mb-4">
              <FourColumnTable
                data={annotatedMentionData || []}
                title={t(
                  "dashboard.topPerformingBrands",
                  "Top Performing Brands by Mentions",
                )}
                column1Key={"brand"}
                column1Title={t("dashboard.brandName", "Brand name")}
                column2Key={"mention_count"}
                column2Title={t("dashboard.mentions", "Mentions")}
                column3Key={"share_of_mention"}
                column3Title={t("dashboard.share", "Share")}
                column4Key={"mention_change"}
                column4Title={t("dashboard.change", "Change")}
                isLoaded={mentionDataIsLoaded}
                error={mentionDataError}
                boxHeight="398px"
                columnRatio="40-20-20-20"
                columnAlignment="l-r-r-r"
                // minCellHeight="4rem"
                itemsPerPage={8}
              />
            </div>
          </div>
          <br />
          <div className="row">
            {/* Left Side */}
            <div className="col-lg-6 mb-4">
              <TwoColumnTable
                data={enhancedMyTopMentions || []}
                title={t("dashboard.myTopMentions", "My Top Mentions")}
                column1Key="mention_text"
                column2Key="mention_count"
                isLoaded={myTopMentionsIsLoaded}
                error={myTopMentionsError}
                boxHeight={"440px"}
                columnRatio="85-15"
                wrapLength={180}
                columnAlignment="l-c"
              />
            </div>
            <div className="col-lg-6 mb-4">
              <FourColumnTable
                data={TopTopicForMentions || []}
                title={t(
                  "dashboard.topTopicsForMentions",
                  "Top Topics for Mentions",
                )}
                column1Key="topic"
                column1Title={t("dashboard.topic", "Topic")}
                column2Key="total_queries"
                column2Title={t("dashboard.prompts", "Prompts")}
                column3Key="current_period_total_mentions"
                column3Title={t("dashboard.mentions", "Mentions")}
                column4Key="current_period_ratio"
                column4Title={t("dashboard.ratio", "Ratio")}
                isLoaded={TopTopicForMentionsIsLoaded}
                error={TopTopicForMentionsError}
                boxHeight="398px"
                columnRatio="55-15-15-15"
                columnAlignment="l-r-r-r"
                minCellHeight="3rem"
              />
            </div>
          </div>
          <br />
          {/* Mention Trend Section */}
          {mention_valid_week_count > TrackingWeekMinCount && (
            <>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <NColumnTable
                    data={mentionTrackingResult}
                    n={8}
                    columnKeys={mentionColumnNames}
                    columnTitles={mentionColumnTitles}
                    columnRatio="30-10-10-10-10-10-10-10"
                    columnAlignment="l-r-r-r-r-r-r-r"
                    title={t("dashboard.mentionTrend", "Mention Trend")}
                    isLoaded={mentionTrendDataIsLoaded}
                    error={mentionTrendDataError}
                    boxHeight="396px"
                  />
                </div>

                <div className="col-lg-6 mb-4">
                  <TrackingChart
                    title={t(
                      "dashboard.mentionTrendOverTime",
                      "Mention Trend Over Time",
                    )}
                    data={mentionTrackingResult}
                    queryColumn="brand_name"
                    dateColumns={mentionDateColumns}
                    isLoaded={mentionTrendDataIsLoaded}
                    error={mentionTrendDataError}
                    height={400}
                  />
                </div>
              </div>
              <br />
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default GEODashboard;
